import { hasPendingApprovalStage } from '../../components/RecentPolicies/Components/makerCheckStage'
import { PARTNERS_GUID_ENUM } from '../../routes/PartnersEnum'
import {
  getPartnerGuid,
  getUserGroups,
  hasAuthourity
} from '../../lib/access.es6'

// One approval dashboard per configured maker-checker stage.
//
// A partner's sequence lives in partner-configs
// (/api/partners/partners_config/{partner}/policy-maker-checks) as
// [{ guid, type, label, sequence, allow_to_skip }]. Those labels are what
// reviewers see on the policy, so they are also the dashboard titles and the
// menu entries — configure a stage and its queue appears.
//
// Each dashboard shows that stage's own work: what is awaiting it, what it
// approved, what it rejected. A policy only ever sits in the Awaiting tab of
// its CURRENT stage — the lowest-sequence check still Pending — so a policy
// that has not cleared level 1 can never surface in the level 2 basket.
//
// Where a partner runs the send-back loop (CoverLink), every reviewer has a
// third option besides approving and rejecting: send the policy BACK for
// changes. That marks `additional_details.need_action` WITHOUT deciding the
// check, so the policy keeps its place in the sequence but leaves the approval
// queues for the Need Action tab, which every level shows. Returning it stamps
// the stage that asked, which puts it in that stage's Corrected policies tab
// for final review rather than back at the end of the queue.

// Partners here fall back to single approval and get no stage dashboards,
// regardless of what maker_checks the policy carries.
// OMM was here from 2026-07-23 to 2026-08-26 pending team alignment on its
// approval steps; now re-enabled (see RecentPoliciesWrapper.js's matching
// Un Confirmed tab restore).
// AccessBank added 2026-09-07: it has never had its own maker-checks stages
// configured (see stages.test.js's "neither configuration nor a built-in
// list" case), but its policies started carrying a non-empty maker_checks
// array once the universal AML Check stage (index.js's Option-B AML sync)
// began applying to every partner. That flipped useMakerChecks on in
// PolicyHeader.js and replaced the plain Approve/Reject buttons with
// MakerChecks, which renders nothing when it finds no actionable Pending
// stage — silently hiding both buttons. Remove once AccessBank's maker-checks
// stages are properly configured (or the AML Check stage is scoped to
// partners that actually run the sequenced flow).
export const MAKER_CHECKS_DISABLED_PARTNERS = [PARTNERS_GUID_ENUM.ACCESS_BANK]

// Partners running the send-back loop. Everywhere else `need_action` means
// something of its own (OMM's single-approval reconfirm flow), so the tab and
// the Request Changes button stay off.
const NEED_ACTION_PARTNERS = [PARTNERS_GUID_ENUM.COVER_LINK]

// What a partner runs when its configuration cannot say — not yet loaded,
// unreadable, or empty because its levels predate the config screen. Keep in
// step with what each partner actually has configured.
const FALLBACK_STAGE_LABELS = {
  [PARTNERS_GUID_ENUM.COVER_LINK]: ['Broker Approval', 'Insurer Approval'],
  [PARTNERS_GUID_ENUM.OLDMUTUAL_KENYA]: [
    'Onboarding Team Approval',
    'Quality Approval',
    'AML Approval',
    'PEP Approval'
  ]
}

// Which side of a hand-off a user works, read off their Keycloak groups.
//
// Roles cannot answer this: ROLE_USER grants CREATE_NEW_POLICY to everyone, so
// an insurer's token carries the capture role too. The groups are clean —
// COVERLINK_BROKER holds the brokers, COVERLINK_INSURER the insurers — and the
// group claim rides in the token.
//
// A stage is matched to a group by the first word of its label: "Broker
// Approval" -> BROKER matches COVERLINK_BROKER. So the pairing follows the
// configured sequence, and a level renamed in partner-config keeps working as
// long as its group is named after it.
const ADMIN_GROUP_MARKERS = ['ADMIN', 'MANAGEMENT', 'SNR_MNGT']

function stageGroupKeyword(stage) {
  return String((stage && stage.label) || '')
    .trim()
    .toUpperCase()
    .split(/[^A-Z0-9]+/)[0]
}

function groupMatches(group, keyword) {
  return !!keyword && String(group).toUpperCase().indexOf(keyword) !== -1
}

function isAdminGroup(group) {
  const name = String(group).toUpperCase()
  return ADMIN_GROUP_MARKERS.some(function(marker) {
    return name.indexOf(marker) !== -1
  })
}

// True when the logged-in user may open this stage's dashboard. Gates the menu
// entry and the route alike, so the other side's queue is a dead end rather
// than a link away.
//
// Three ways in: an administrator sees every level; a user whose groups name a
// level of this sequence is held to those levels, and sees no other; and
// everyone else — nobody the group scheme describes — falls back to the level's
// own permission, so a reviewer outside the broker/insurer groups still reaches
// the queue their role entitles them to rather than losing it to a naming
// convention that says nothing about them.
export function canViewApprovalStage(stage, stages = []) {
  if (!stage) {
    return false
  }
  if (!stage.usesNeedAction) {
    return hasAuthourity(stage.authority)
  }

  const groups = getUserGroups()
  if (groups.some(isAdminGroup)) {
    return true
  }

  const keyword = stageGroupKeyword(stage)
  const typed = groups.some(function(group) {
    return stages.some(function(candidate) {
      return groupMatches(group, stageGroupKeyword(candidate))
    })
  })
  if (!typed) {
    return hasAuthourity(stage.authority)
  }

  return groups.some(function(group) {
    return groupMatches(group, keyword)
  })
}

// URL-safe id for a stage, derived from its label so the route survives a
// re-ordering of the sequence: "Onboarding Team Approval" →
// "onboarding-team-approval".
export function stageKey(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function emptyMessageFor(title, previousTitle) {
  const base = `No policies awaiting ${title}.`
  if (!previousTitle) {
    return base
  }
  return `${base} Policies reach this queue only once ${previousTitle} has been granted.`
}

// Build the stage list for a partner from its configured sequence.
//
// The fallback applies whenever the configuration yields nothing — not just
// before it loads. A partner can run a sequence its policies carry without
// ever having rows in partner-configs (CoverLink's levels predate that CRUD
// screen), and treating an empty response as "this partner has no approval
// levels" took the dashboards and their menu entries away from it.
export function buildApprovalStages(options = {}) {
  const partnerGuid = options.partnerGuid || getPartnerGuid()
  if (MAKER_CHECKS_DISABLED_PARTNERS.indexOf(partnerGuid) !== -1) {
    return []
  }

  const configured = Array.isArray(options.makerChecks)
    ? options.makerChecks
        .slice()
        .sort(function(a, b) {
          return (a.sequence || 0) - (b.sequence || 0)
        })
        .map(function(check) {
          return check.label
        })
        .filter(Boolean)
    : []

  const labels = configured.length
    ? configured
    : FALLBACK_STAGE_LABELS[partnerGuid] || []

  const usesNeedAction = NEED_ACTION_PARTNERS.indexOf(partnerGuid) !== -1
  const seen = {}

  return labels
    .filter(function(label) {
      // A stage saved without a label has nothing to title a dashboard with
      // and nothing to filter the queue on.
      if (!label || seen[stageKey(label)]) {
        return false
      }
      seen[stageKey(label)] = true
      return true
    })
    .map(function(label, index, all) {
      return {
        key: stageKey(label),
        title: label,
        label: label,
        sequenceIndex: index,
        emptyMessage: emptyMessageFor(label, index > 0 ? all[index - 1] : null),
        usesNeedAction: usesNeedAction,
        // Every level can ask for changes and every level carries the Need
        // Action basket: the level that asked needs to see what it is waiting
        // on as much as the level correcting it needs to see what to fix.
        receivesNeedAction: usesNeedAction,
        // A correction comes back to the level that asked for it, so only the
        // levels that review someone else's capture have one to show.
        showsCorrected: usesNeedAction && index > 0,
        canRequestChanges: usesNeedAction,
        // What a level asks of someone its groups do not describe. The capture
        // level is the one users reach without the power to approve.
        authority:
          usesNeedAction && index === 0 ? 'CREATE_NEW_POLICY' : 'APPROVE_POLICY'
      }
    })
}

// True when the policy is sitting on a stage that can ask for changes rather
// than only approve or reject. Every level of a partner running the loop can:
// a broker spotting a problem in what they captured has the same need to park
// the policy for correction as an insurer does.
export function stageCanRequestChanges(policy) {
  if (NEED_ACTION_PARTNERS.indexOf(getPartnerGuid()) === -1) {
    return false
  }
  return hasPendingApprovalStage(policy)
}

// Routes CoverLink's dashboards carried before stage keys were derived from
// labels. Kept so links already in circulation still resolve.
const LEGACY_STAGE_KEYS = {
  broker: 'broker-approval',
  insurer: 'insurer-approval'
}

// Resolve the stage a route's :stage param refers to, or null when unknown.
export function findApprovalStage(key, stages = []) {
  const wanted = LEGACY_STAGE_KEYS[key] || key
  const match = stages.filter(function(stage) {
    return stage.key === wanted
  })
  return match.length ? match[0] : null
}

// The tabs one stage's dashboard shows: its queue, its decisions, and — where
// the partner runs the send-back loop — the policies out for changes and the
// corrections waiting on this level.
//
// `query` is what the server is asked for; `needAction`/`makerStage` are the
// client-side refinement on top of it, because maker_check_status matches a
// policy with SOME check in that status, not the check a reviewer can act on
// today (a policy queued behind an earlier stage has both Pending).
export function approvalStageTabs(stage) {
  if (!stage) {
    return []
  }

  const tabs = [
    {
      key: 'queue',
      label: 'Awaiting Approval',
      query: { maker_check_label: stage.label, maker_check_status: 'Pending' },
      // A policy out for changes belongs to whoever is correcting it until
      // they return it, so it must not sit in an approval queue meanwhile.
      needAction: stage.usesNeedAction ? 'exclude' : undefined,
      // Only where a Corrected tab catches them: otherwise a returned policy,
      // which clears need_action but keeps its corrected_for_stage stamp, would
      // sit in no tab at all.
      makerStage: stage.showsCorrected
        ? { ...stage, excludeCorrected: true }
        : stage,
      emptyMessage: stage.emptyMessage
    }
  ]

  if (stage.receivesNeedAction) {
    tabs.push({
      key: 'need-action',
      label: 'Need Action',
      // Filtered to this stage's label: a policy sent back for changes keeps
      // its pending check at the level that asked, so each dashboard's Need
      // Action tab shows only the policies that belong to it.
      query: {
        maker_check_label: stage.label,
        maker_check_status: 'Pending',
        additional_details: { need_action: true }
      },
      needAction: 'only',
      makerStage: 'pendingApproval',
      emptyMessage: 'No policies have been sent back for changes.'
    })
  }

  if (stage.showsCorrected) {
    // Corrections come back to the level that asked for them: returning a
    // policy stamps the stage it was sitting on, and that stage is still its
    // pending one. Approving moves the policy on, at which point the stamp no
    // longer matches and it rejoins the ordinary queue of the next level.
    tabs.push({
      key: 'corrected',
      label: 'Corrected policies',
      query: {
        maker_check_label: stage.label,
        maker_check_status: 'Pending',
        additional_details: { corrected_for_stage: stage.label }
      },
      needAction: 'exclude',
      makerStage: { ...stage, match: 'corrected' },
      emptyMessage: `No corrected policies awaiting final review at ${stage.title}.`
    })
  }

  return tabs.concat([
    {
      key: 'approved',
      label: 'Approved',
      query: { maker_check_label: stage.label, maker_check_status: 'Approved' },
      makerStage: { ...stage, match: 'Approved' },
      decisionStage: stage,
      emptyMessage: `No policies approved at ${stage.title} yet.`
    },
    {
      key: 'rejected',
      label: 'Rejected',
      query: { maker_check_label: stage.label, maker_check_status: 'Rejected' },
      makerStage: { ...stage, match: 'Rejected' },
      decisionStage: stage,
      emptyMessage: `No policies rejected at ${stage.title}.`
    }
  ])
}
